import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Modal, Select, Radio, Space, Switch, Input, Flex, Typography, notification, Alert, Tag } from "antd";
import { useSelector } from "react-redux";
import { EyeOutlined, EyeInvisibleOutlined, WarningOutlined } from "@ant-design/icons";
import CustomCssTransition from "../UI/CustomCssTransition/customCssTransition";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

function RuleModal({ isOpen, currentRule, onSave, onClose, existingRules = [] }) {
  const [ruleFormData, setRuleFormData] = useState({
    trade: null,
    serviceType: 'all',
    jobs: [],
    message: '',
    isActive: true,
    showForMembers: true
  });
  const [conflictError, setConflictError] = useState(null);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const conflictAlertRef = useRef(null);
  
  const { organizationData } = useSelector((state) => state?.organization);
  const { jobs = [] } = useSelector((state) => state.jobs);
  const { trades } = useSelector((state) => state.trade);

  const getRuleId = (rule) => rule?._id || rule?.id;

  const tradeHashMap = useMemo(() => {
    const map = {};
    organizationData?.navigation_mapping
      ?.filter(trade => trade.isActive === true)
      ?.forEach(navTrade => {
        const orgTrade = trades.find(t => t.tradeName === navTrade.trade.name);
        if (orgTrade) {
          const availableServices = {};
          if (navTrade.offeredServiceTypes.repair) {
            availableServices.repair = { label: "Repair", value: "repair" };
          }
          if (navTrade.offeredServiceTypes.estimate) {
            availableServices.estimate = { label: "Estimate", value: "estimate" };
          }
          if (navTrade.offeredServiceTypes.maintenance) {
            availableServices.maintenance = { label: "Maintenance", value: "maintenance" };
          }
          const additionalServices = navTrade.offeredServiceTypes.additionalServices || {};
          Object.keys(additionalServices).forEach(serviceName => {
            if (additionalServices[serviceName]) {
              availableServices[serviceName] = {
                label: serviceName.charAt(0).toUpperCase() + serviceName.slice(1),
                value: serviceName
              };
            }
          });
          map[orgTrade.tradeName] = {
            tradeName: orgTrade.tradeName,
            tradeId: orgTrade._id,
            _id: orgTrade._id,
            services: availableServices
          };
        }
      });
    return map;
  }, [organizationData?.navigation_mapping, trades]);

  const activeTrades = useMemo(() => {
    return Object.keys(tradeHashMap).map(tradeName => ({
      label: tradeName,
      value: tradeName
    }));
  }, [tradeHashMap]);

  const getActiveServices = useCallback(() => {
    if (!ruleFormData.trade || !tradeHashMap[ruleFormData.trade]) {
      return [];
    }
    return Object.values(tradeHashMap[ruleFormData.trade].services);
  }, [ruleFormData.trade, tradeHashMap]);

  const reducedJobsData = useMemo(() => {
    const reducedData = {};
    jobs?.forEach(job => {
      const { trade, serviceType, jobItem } = job;
      const { tradeName } = trade;
      if (!tradeHashMap[tradeName]) return;

      if (!reducedData[tradeName]) {
        reducedData[tradeName] = {};
      }

      const serviceTypeLower = serviceType.toLowerCase();
      if (!tradeHashMap[tradeName].services[serviceTypeLower]) return;

      if (!reducedData[tradeName][serviceTypeLower]) {
        reducedData[tradeName][serviceTypeLower] = [];
      }

      reducedData[tradeName][serviceTypeLower].push(jobItem);
    });
    return reducedData;
  }, [jobs, tradeHashMap]);

  const getAvailableJobsWithService = useCallback(() => {
    if (!ruleFormData.trade) return [];

    if (ruleFormData.serviceType === 'all') {
      const jobsWithService = [];
      const tradeData = reducedJobsData[ruleFormData.trade] || {};

      Object.entries(tradeData).forEach(([serviceName, jobsList]) => {
        jobsList.forEach(job => {
          jobsWithService.push({
            job: job,
            service: serviceName,
            uniqueKey: `${serviceName}::${job}`
          });
        });
      });

      return jobsWithService;
    } else {
      const jobsList = reducedJobsData[ruleFormData.trade]?.[ruleFormData.serviceType] || [];
      return jobsList.map(job => ({
        job: job,
        service: ruleFormData.serviceType,
        uniqueKey: job
      }));
    }
  }, [ruleFormData.trade, ruleFormData.serviceType, reducedJobsData]);

  const servicesOverlap = useCallback((leftService, rightService) => {
    return leftService === 'all' || rightService === 'all' || leftService === rightService;
  }, []);

  const parseJobSelection = useCallback((serviceType, rawJob) => {
    if (typeof rawJob !== 'string') {
      return null;
    }
    if (rawJob.includes('::')) {
      const [jobService, ...rest] = rawJob.split('::');
      const jobName = rest.join('::');
      return { service: jobService || serviceType, job: jobName || rawJob };
    }
    return { service: serviceType === 'all' ? 'all' : serviceType, job: rawJob };
  }, []);

  const normalizeRuleSelections = useCallback((serviceType, jobsList) => {
    const normalizedJobs = (jobsList || [])
      .map((rawJob) => parseJobSelection(serviceType, rawJob))
      .filter(Boolean);
    return {
      isAllJobs: normalizedJobs.length === 0,
      jobs: normalizedJobs
    };
  }, [parseJobSelection]);

  const getRuleOverlapDetails = useCallback((leftRule, rightRule) => {
    if (!servicesOverlap(leftRule.serviceType, rightRule.serviceType)) {
      return { hasConflict: false, overlappingJobs: [] };
    }

    const leftSelections = normalizeRuleSelections(leftRule.serviceType, leftRule.jobs);
    const rightSelections = normalizeRuleSelections(rightRule.serviceType, rightRule.jobs);

    if (leftSelections.isAllJobs && rightSelections.isAllJobs) {
      return { hasConflict: true, overlappingJobs: ['All Jobs'] };
    }

    if (leftSelections.isAllJobs) {
      const overlappingJobs = rightSelections.jobs
        .filter((rightJob) => servicesOverlap(leftRule.serviceType, rightJob.service))
        .map((rightJob) => rightJob.job);
      return {
        hasConflict: overlappingJobs.length > 0,
        overlappingJobs: [...new Set(overlappingJobs)]
      };
    }

    if (rightSelections.isAllJobs) {
      const overlappingJobs = leftSelections.jobs
        .filter((leftJob) => servicesOverlap(rightRule.serviceType, leftJob.service))
        .map((leftJob) => leftJob.job);
      return {
        hasConflict: overlappingJobs.length > 0,
        overlappingJobs: [...new Set(overlappingJobs)]
      };
    }

    const overlappingJobs = leftSelections.jobs
      .filter((leftJob) =>
        rightSelections.jobs.some((rightJob) =>
          leftJob.job === rightJob.job &&
          servicesOverlap(leftJob.service, rightJob.service)
        )
      )
      .map((leftJob) => leftJob.job);

    return {
      hasConflict: overlappingJobs.length > 0,
      overlappingJobs: [...new Set(overlappingJobs)]
    };
  }, [normalizeRuleSelections, servicesOverlap]);

  const checkJobConflicts = useCallback((trade, serviceType, selectedJobs, currentRuleId) => {
    if (!trade || !serviceType) return null;

    const candidateRule = {
      trade,
      serviceType,
      jobs: selectedJobs || []
    };

    const conflictingRules = existingRules.filter((rule) => {
      const ruleId = getRuleId(rule);
      if (currentRuleId && String(ruleId) === String(currentRuleId)) {
        return false;
      }
      if (rule.trade !== trade) {
        return false;
      }
      return getRuleOverlapDetails(candidateRule, rule).hasConflict;
    });

    if (conflictingRules.length === 0) {
      return null;
    }

    const overlappingJobs = [...new Set(
      conflictingRules.flatMap((rule) => getRuleOverlapDetails(candidateRule, rule).overlappingJobs)
    )];
    const primaryRule = conflictingRules[0];

    return {
      rule: primaryRule,
      rules: conflictingRules,
      jobs: overlappingJobs.length > 0 ? overlappingJobs : ['All Jobs'],
      ruleId: getRuleId(primaryRule)
    };
  }, [existingRules, getRuleOverlapDetails]);


  useEffect(() => {
    if (!isOpen) {
      setConflictError(null);
      setHasAttemptedSave(false);
      return;
    }

    if (currentRule) {
      const newFormData = {
        _id: getRuleId(currentRule),
        trade: currentRule.trade,
        serviceType: currentRule.serviceType,
        jobs: currentRule.jobs || [],
        message: currentRule.message,
        isActive: currentRule.isActive,
        showForMembers: currentRule.showForMembers ?? true
      };
      setRuleFormData(newFormData);
      setConflictError(null);
      setHasAttemptedSave(false);
    } else {
      const firstTrade = activeTrades[0]?.value || null;
      setRuleFormData({
        trade: firstTrade,
        serviceType: 'all',
        jobs: [],
        message: '',
        isActive: true,
        showForMembers: true
      });
      setConflictError(null);
      setHasAttemptedSave(false);
    }
  }, [isOpen, currentRule, activeTrades, checkJobConflicts]);


  useEffect(() => {
    if (conflictError && conflictAlertRef.current) {
      setTimeout(() => {
        conflictAlertRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [conflictError]);

  const updateFormData = useCallback((updates) => {
    const newFormData = { ...ruleFormData, ...updates };
    setRuleFormData(newFormData);
    setConflictError(null);
  }, [ruleFormData]);

  const handleSave = () => {
    setHasAttemptedSave(true);

    if (!ruleFormData.trade) {
      notification.error({
        message: 'Error',
        description: 'Please select a trade',
        duration: 3,
        placement: 'topRight'
      });
      return;
    }

    if (!ruleFormData.message.trim()) {
      notification.error({
        message: 'Error',
        description: 'Please enter a message',
        duration: 3,
        placement: 'topRight'
      });
      return;
    }

    const conflict = checkJobConflicts(
      ruleFormData.trade,
      ruleFormData.serviceType,
      ruleFormData.jobs,
      ruleFormData._id
    );
    setConflictError(conflict);

    if (conflict) {
      notification.error({
        message: 'Conflict Detected',
        description: 'Please resolve job conflicts before saving',
        duration: 3,
        placement: 'topRight'
      });
      conflictAlertRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      return;
    }

    const processedJobs = ruleFormData.serviceType === 'all'
      ? ruleFormData.jobs
      : ruleFormData.jobs.map(j => {
        if (j.includes('::')) {
          return j.split('::')[1];
        }
        return j;
      });

    const ruleData = {
      ...ruleFormData,
      jobs: processedJobs
    };

    if (currentRule) {
      ruleData._id = getRuleId(currentRule);
    }

    onSave(ruleData);
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0, fontSize: "18px" }}>
          {currentRule ? "Edit Rule" : "Add New Rule"}
        </Title>
      }
      open={isOpen}
      onOk={handleSave}
      onCancel={onClose}
      width={600}
      okText={currentRule ? "Update Rule" : "Add Rule"}
      cancelText="Cancel"
      styles={{
        body: {
          minHeight: '520px',
          maxHeight: '70vh',
          overflowY: 'auto',
          paddingRight: '12px',
          paddingBottom: '8px',
          scrollbarGutter: 'stable'
        }
      }}
      destroyOnClose={true}
    >
      <div style={{ padding: "20px 0" }}>
        <CustomCssTransition
          in={hasAttemptedSave && !!conflictError}
          timeout={200}
          classNames="glimpse"
          unmountOnExit
          appear={false}
        >
          <div ref={conflictAlertRef}>
            <Alert
              message="Job Conflict Detected"
              description={
                <Space direction="vertical" size={4}>
                  <Text style={{ fontSize: "13px" }}>
                    The following job(s) are already assigned in another rule:
                  </Text>
                  <div style={{ marginTop: "4px" }}>
                    {conflictError?.jobs?.map(job => (
                      <Tag color="red" key={job} style={{ marginBottom: "4px" }}>
                        {job}
                      </Tag>
                    ))}
                  </div>
                  <Text type="secondary" style={{ fontSize: "12px", marginTop: "4px", display: "block" }}>
                    {conflictError?.rules?.length > 1 ? (
                      <>Conflicts found in {conflictError.rules.length} existing rules</>
                    ) : (
                      <>
                        Existing rule: {conflictError?.rule.trade} → {
                          conflictError?.rule?.serviceType === 'all'
                            ? 'All Services'
                            : conflictError?.rule?.serviceType
                        }
                        {conflictError?.ruleId && (
                          <> (ID: {String(conflictError.ruleId).slice(-6)})</>
                        )}
                      </>
                    )}
                  </Text>
                  <Text type="danger" style={{ fontSize: "12px", marginTop: "8px", display: "block" }}>
                    ⚠️ Please remove these jobs from the other rule first, or select different jobs.
                  </Text>
                </Space>
              }
              type="error"
              showIcon
              icon={<WarningOutlined />}
              style={{ marginBottom: "20px" }}
            />
          </div>
        </CustomCssTransition>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontWeight: "600", fontSize: "14px", display: "block", marginBottom: "8px" }}>
            Trade
          </label>
          <Select
            value={ruleFormData.trade}
            onChange={(value) => updateFormData({ trade: value, serviceType: 'all', jobs: [] })}
            style={{ width: "100%" }}
            size="large"
            placeholder="Select a trade"
          >
            {activeTrades.map((trade) => (
              <Option key={trade.value} value={trade.value}>
                {trade.label}
              </Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontWeight: "600", fontSize: "14px", display: "block", marginBottom: "8px" }}>
            Service Type
          </label>
          <Radio.Group
            value={ruleFormData.serviceType}
            onChange={(e) => updateFormData({ serviceType: e.target.value, jobs: [] })}
            style={{ width: "100%" }}
            disabled={!ruleFormData.trade}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Radio value="all">All Services</Radio>
              {getActiveServices().map((service) => (
                <Radio key={service.value} value={service.value}>
                  {service.label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
          {ruleFormData.trade && getActiveServices().length === 0 && (
            <Text type="warning" style={{ fontSize: "12px", display: "block", marginTop: "4px" }}>
              No services available for this trade
            </Text>
          )}
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontWeight: "600", fontSize: "14px", display: "block", marginBottom: "8px" }}>
            Jobs
          </label>
          <Select
            mode="multiple"
            value={ruleFormData.jobs}
            onChange={(value) => updateFormData({ jobs: value })}
            style={{ width: "100%" }}
            size="large"
            placeholder={ruleFormData.serviceType === 'all' ? "Leave empty for all jobs" : "Select specific jobs"}
            disabled={!ruleFormData.trade}
            optionLabelProp="label"
            tagRender={(props) => {
              const { label, value, closable, onClose } = props;
              let jobName = label;
              let serviceName = null;

              if (value.includes('::')) {
                const parts = value.split('::');
                serviceName = parts[0];
                jobName = parts[1];
              } else if (ruleFormData.serviceType !== 'all') {
                serviceName = ruleFormData.serviceType;
              }

              return (
                <Tag
                  closable={closable}
                  onClose={onClose}
                  style={{
                    marginRight: 4,
                    marginBottom: 4,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    paddingRight: closable ? '4px' : '8px'
                  }}
                >
                  <span>{jobName}</span>
                  {(serviceName && ruleFormData.serviceType === 'all') && (
                    <Tag
                      color="blue"
                      style={{
                        margin: 0,
                        fontSize: "10px",
                        textTransform: "capitalize",
                        padding: "0 6px",
                        lineHeight: "18px",
                        height: '18px',
                        display: 'inline-flex',
                        alignItems: 'center'
                      }}
                    >
                      {serviceName}
                    </Tag>
                  )}
                </Tag>
              );
            }}
          >
            {getAvailableJobsWithService().map((jobData) => (
              <Option
                key={jobData.uniqueKey}
                value={jobData.uniqueKey}
                label={jobData.job}
              >
                {ruleFormData.serviceType === 'all' ? (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                    <span>{jobData.job}</span>
                    <Tag
                      color="blue"
                      style={{
                        margin: 0,
                        fontSize: "11px",
                        textTransform: "capitalize",
                        marginLeft: "8px"
                      }}
                    >
                      {jobData.service}
                    </Tag>
                  </div>
                ) : (
                  <span>{jobData.job}</span>
                )}
              </Option>
            ))}
          </Select>
            <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: "4px" }}>
              No jobs selected - rule will apply to all jobs
            </Text>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontWeight: "600", fontSize: "14px", display: "block", marginBottom: "8px" }}>
            Travel Add-On Notice
          </label>
          <TextArea
            value={ruleFormData.message}
            onChange={(e) => updateFormData({ message: e.target.value })}
            placeholder="Enter the message to display for this rule..."
            rows={4}
            maxLength={150}
            showCount
          />
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ fontWeight: "600", fontSize: "14px", display: "block", marginBottom: "8px" }}>
            Member Visibility
          </label>
          <div
            className="flex items-center justify-between w-full p-3 rounded-lg"
            style={{
              backgroundColor: ruleFormData.showForMembers ? "#f6ffed" : "#fff1f0",
              border: `1px solid ${ruleFormData.showForMembers ? "#b7eb8f" : "#ffa39e"}`,
              transition: "all 0.3s ease"
            }}
          >
            <div className="flex items-center gap-3">
              <div style={{
                width: "36px",
                height: "36px",
                borderRadius: "6px",
                backgroundColor: ruleFormData.showForMembers ? "#52c41a" : "#ff4d4f",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.3s ease"
              }}>
                {ruleFormData.showForMembers ? (
                  <EyeOutlined style={{ color: "white", fontSize: "16px" }} />
                ) : (
                  <EyeInvisibleOutlined style={{ color: "white", fontSize: "16px" }} />
                )}
              </div>
              <div>
                <div style={{
                  fontWeight: "500",
                  fontSize: "13px",
                  color: "#262626",
                  marginBottom: "2px"
                }}>
                  {ruleFormData.showForMembers ? "Visible to Members" : "Hidden from Members"}
                </div>
                <div style={{
                  fontSize: "12px",
                  color: "#8c8c8c",
                  lineHeight: "1.3"
                }}>
                  {ruleFormData.showForMembers
                    ? "Members can see this message"
                    : "Members cannot see this message"}
                </div>
              </div>
            </div>
            <Switch
              checked={ruleFormData.showForMembers}
              onChange={(checked) => updateFormData({ showForMembers: checked })}
            />
          </div>
          <Alert
            message={
              <Text style={{ fontSize: "12px" }}>
                {ruleFormData.showForMembers
                  ? "💡 This message will be displayed to both members and non-members"
                  : "💡 This message will only be shown to non-members (e.g., travel add-on waived for members)"}
              </Text>
            }
            type="info"
            showIcon={false}
            style={{ marginTop: "8px", padding: "8px 12px" }}
          />
        </div>

        <div>
          <Flex align="center" gap={8}>
            <Switch
              checked={ruleFormData.isActive}
              onChange={(checked) => updateFormData({ isActive: checked })}
            />
            <Text style={{ fontSize: "14px" }}>
              {ruleFormData.isActive ? "Rule is active" : "Rule is inactive"}
            </Text>
          </Flex>
        </div>
      </div>
    </Modal>
  );
}

export default RuleModal;
